import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, skipWhile, Subject, take } from 'rxjs';
import { ApiUsersService } from '../../requests/api-users/api-users.component';
import { ERRORS, PROCESS_SERVICE_STATES } from '../../../_constants/values/values.service';
import { ApiProxyUsersService } from '../../requests/api-proxy-users/api-proxy-users.component';
import { ROLES } from '../../../../models/Core.model';


@Injectable({
    providedIn: 'root'
})
export class UsersService {

    readonly onlistUsers$: BehaviorSubject<string> = new BehaviorSubject<string>(PROCESS_SERVICE_STATES.waiting);
    readonly onlistUsersTypes$: BehaviorSubject<string> = new BehaviorSubject<string>(PROCESS_SERVICE_STATES.waiting);
    readonly onlistRoles$: BehaviorSubject<string> = new BehaviorSubject<string>(PROCESS_SERVICE_STATES.waiting);
    readonly onGetInfo$: BehaviorSubject<string> = new BehaviorSubject<string>(PROCESS_SERVICE_STATES.waiting);
    readonly onlistInfo$: BehaviorSubject<string> = new BehaviorSubject<string>(PROCESS_SERVICE_STATES.waiting);
    private markToUpdateUsers = true;
    private markToUpdateUsersTypes = true;
    private markToUpdateRoles = true;
    private markToUpdateUserInfo = true;
    private markToUpdateInfo = true;
    private info: any = null;
    private users: any[] = [];
    private usersObject: any = {};
    private usersTypes: any[] = [];
    private usersTypesObject: any = {};
    private usersChanged$ = new Subject<void>();
    private roles: any[] = [];
    private rolesObject: any = {};
    private roleHashesByValue: any = {};
    private rolesByValue: any = {};
    private userInfo: any = null;

    constructor(
        private readonly apiUsersService: ApiUsersService,
        private readonly apiProxyUsersService: ApiProxyUsersService
    ) { }

    public subscribeToUsers() {
        return this.usersChanged$.asObservable();
    }

    public listInfo(): Observable<any> {
        // if (!this.markToUpdateInfo) {
        //     return of(this.info);
        // }

        if (this.onlistInfo$.value === PROCESS_SERVICE_STATES.inProgress) {
            return this.onlistInfo$.asObservable()
                .pipe(
                    skipWhile((res: any) => res !== PROCESS_SERVICE_STATES.done),
                    take(1)
                );
        }

        this.onlistInfo$.next(PROCESS_SERVICE_STATES.inProgress);
        return this.apiUsersService.listInfo()
            .pipe(
                map((resp: any) => {
                    if (resp) {
                        this.info = resp;
                        this.markToUpdateInfo = false;
                        this.onlistInfo$.next(PROCESS_SERVICE_STATES.done);
                        return resp;
                    } else {
                        this.markToUpdateInfo = true;
                        this.onlistInfo$.next(PROCESS_SERVICE_STATES.done);
                        throw ERRORS.malformedRequest;
                    }
                }),
                catchError((error: object) => {
                    this.markToUpdateInfo = true;
                    this.onlistInfo$.next(PROCESS_SERVICE_STATES.done);
                    throw error;
                })
            );
    }

    public getInfo(): any {
        return this.info;
    }

    public getUserName(): string {
        const info = this.info?.result || this.info;
        return info?.user_name ?? info?.name ?? '';
    }

    public getUserEmail(): string {
        const info = this.info?.result || this.info;
        return info?.user_email ?? info?.email ?? '';
    }

    public markInfoToUpdate(): void {
        this.markToUpdateInfo = true;
    }

    /**
    * List all users
    * @returns The users
    */
    public list(): Observable<any> {
        if (!this.markToUpdateUsers) {
            return of(this.users);
        }

        if (this.onlistUsers$.value === PROCESS_SERVICE_STATES.inProgress) {
            return this.onlistUsers$.asObservable()
                .pipe(
                    skipWhile((res: any) => res !== PROCESS_SERVICE_STATES.done),
                    take(1)
                );
        } else {
            this.onlistUsers$.next(PROCESS_SERVICE_STATES.inProgress);
            return this.apiUsersService.list()
                .pipe(
                    map((resp: any) => {
                        if (resp) {
                            const auxUsersObject: any = {};
                            for (const user of resp) {
                                auxUsersObject[user.id] = user;
                            }
                            this.usersObject = auxUsersObject;
                            this.users = resp;
                            this.markToUpdateUsers = false;
                            this.onlistUsers$.next(PROCESS_SERVICE_STATES.done);
                            return true;
                        } else {
                            this.markToUpdateUsers = true;
                            this.onlistUsers$.next(PROCESS_SERVICE_STATES.done);
                            throw ERRORS.malformedRequest;
                        }
                    }),
                    catchError((error: object) => {
                        this.markToUpdateUsers = true;
                        this.onlistUsers$.next(PROCESS_SERVICE_STATES.done);
                        throw error;
                    })
                );
        }
    }

    public listTypes(): Observable<any> {
        if (!this.markToUpdateUsersTypes) {
            return of(this.usersTypes);
        }

        if (this.onlistUsersTypes$.value === PROCESS_SERVICE_STATES.inProgress) {
            return this.onlistUsersTypes$.asObservable()
                .pipe(
                    skipWhile((res: any) => res !== PROCESS_SERVICE_STATES.done),
                    take(1)
                );
        } else {
            this.onlistUsersTypes$.next(PROCESS_SERVICE_STATES.inProgress);
            return this.apiUsersService.listTypes()
                .pipe(
                    map((resp: any) => {
                        if (resp) {
                            for (const type of resp) {
                                this.usersTypesObject[type.id] = type;
                            }
                            this.usersTypes = resp;
                            this.markToUpdateUsersTypes = false;
                            this.onlistUsersTypes$.next(PROCESS_SERVICE_STATES.done);
                        } else {
                            this.markToUpdateUsersTypes = true;
                            this.onlistUsersTypes$.next(PROCESS_SERVICE_STATES.done);
                            return of(ERRORS.malformedRequest);
                        }
                        return true;
                    }),
                    catchError((error: object) => {
                        throw error;
                    })
                );
        }
    }

    public getUsersTypes(): any {
        return this.usersTypes;
    }

    public getUsersType(id: number): any {
        return this.usersTypesObject[id];
    }

    public getUsers(): any {
        return this.users;
    }

    public listRoles(): Observable<any> {
        if (!this.markToUpdateRoles) {
            return of(this.roles);
        }

        if (this.onlistRoles$.value === PROCESS_SERVICE_STATES.inProgress) {
            return this.onlistRoles$.asObservable()
                .pipe(
                    skipWhile((res: any) => res !== PROCESS_SERVICE_STATES.done),
                    take(1)
                );
        }

        this.onlistRoles$.next(PROCESS_SERVICE_STATES.inProgress);
        return this.apiUsersService.listRoles()
            .pipe(
                map((resp: any) => {
                if (resp) {
                    for (const role of resp) {
                        const hash = this.hashString(role.value);
                        this.roleHashesByValue[role.value] = hash;
                        this.rolesObject[hash] = role;
                        this.rolesByValue[role.value] = role;
                    }
                    this.roles = resp;
                    this.markToUpdateRoles = false;
                    this.onlistRoles$.next(PROCESS_SERVICE_STATES.done);
                } else {
                    this.markToUpdateRoles = true;
                    this.onlistRoles$.next(PROCESS_SERVICE_STATES.done);
                    return of(ERRORS.malformedRequest);
                }
                return true;
            }),
            catchError((error: object) => {
                throw error;
            })
        );
    }

    public hashString(str: string): number {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash);
    }

    public getRoleHashByValue(value: string): number {
        return this.roleHashesByValue[value];
    }

    public getRoleByValue(value: string): any {
        return this.rolesByValue[value];
    }

    public getRoles(): any[] {
        return this.roles;
    }

    public getRole(id: number): any {
        return this.rolesObject[id];
    }

    public getUserInfo(): any {
        return this.userInfo;
    }

    public createUser(data: any): Observable<any> {
        return this.apiUsersService.createUser(data).pipe(
            map((resp: any) => {
                if (resp) {
                    const _user = {
                        ...data,
                        id: resp.user_id,
                        user_is_external: data.isExternal,
                        user_is_suspended: data.isSuspended
                    };
                    this.usersObject[resp.user_id] = _user;
                    this.users.push(_user);
                    this.markToUpdateUsers = true;
                    this.usersChanged$.next();
                    return _user;
                } else {
                    throw new Error(ERRORS.malformedRequest);
                }
            }),
            catchError((error: object) => {
                throw error;
            })
        );
    }

    public updateUser(data: any): Observable<any> {
        return this.apiUsersService.updateUser(data).pipe(
            map((resp: any) => {
                if (resp) {
                    const _user = {
                        ...data,
                        id: data.user_id,
                        user_is_external: data.isExternal,
                        user_is_suspended: data.isSuspended
                    };
                    const index = this.users.findIndex((user: any) => user.id === data.user_id);
                    if (index !== -1) {
                        this.users[index] = _user;
                    }
                    this.usersObject[data.user_id] = _user;
                    this.usersChanged$.next();
                } else {
                    throw new Error(ERRORS.malformedRequest);
                }
                return true;
            }),
            catchError((error: object) => {
                throw error;
            })
        );
    }

    public setClients(userId: number, clients: number[]): Observable<any> {
        return this.apiProxyUsersService.setClients(userId, clients).pipe(
            map((resp: any) => {
                const user = this.usersObject[userId];
                if (user) {
                    user.client_ids = clients;
                }
                const index = this.users.findIndex((item: any) => item.id === userId);
                if (index !== -1) {
                    this.users[index] = {
                        ...this.users[index],
                        client_ids: clients
                    };
                }
                this.usersChanged$.next();
                return resp;
            }),
            catchError((error: object) => {
                throw error;
            })
        );
    }

    public deleteUser(userId: number | string): Observable<any> {
        return this.apiUsersService.deleteUser(userId).pipe(
            map((resp: any) => {
                // remove deleted user from this.users with splice
                const index = this.users.findIndex((user: any) => user.id === userId);
                if (index !== -1) {
                    this.users.splice(index, 1);
                }
                delete this.usersObject[userId];
                this.usersChanged$.next();
                return resp;
            }),
            catchError((error: object) => {
                throw error;
            })
        );
    }

    public getUser(userId: number): any {
        return this.usersObject[userId];
    }

    public markToUpdate(): void {
        this.markToUpdateUsers = true;
        this.markToUpdateUserInfo = true;
    }

    public regenerateToken(email: string): Observable<any> {
        return this.apiUsersService.regenerateToken(email)
            .pipe(
                map((resp: any) => resp),
                catchError((error: object) => {
                    throw error;
                })
            );
    }

    public isAgent(): boolean {
        return this.info?.roles?.includes(ROLES.AGENT);
    }

    public isCustomer(): boolean {
        return this.info?.roles?.includes(ROLES.CUSTOMER);
    }

}
